import { cn } from "@/lib/utils";
import * as React from "react";
import { InputVariantProps, inputVariants } from "./input-variants";

export type InputProps = Omit<
  InputVariantProps,
  "error" | "prefix" | "suffix"
> &
  Omit<React.ComponentProps<"input">, "prefix"> & {
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    placeholder?: string;
    errorMsg?: string;
    testId?: string;
    className?: string;
    fullWidth?: boolean;
  };

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      prefix,
      suffix,
      errorMsg,
      testId = "input",
      className,
      fullWidth = false,
      type = "text",
      ...inputProps
    },
    ref,
  ) => {
    return (
      <div
        className={cn("tw-body-2 tw-relative", {
          "tw-w-full tw-flex-1": fullWidth,
        })}
      >
        {!!prefix && (
          <span
            data-testid={`${testId}-prefix`}
            className={cn(
              "tw-h-full tw-inline-flex tw-absolute tw-top-0 tw-left-12 tw-items-center tw-justify-center tw-w-20",
              {
                "tw-text-gray-500": inputProps.disabled,
              },
            )}
          >
            {prefix}
          </span>
        )}
        <input
          data-testid={testId}
          aria-invalid={!!errorMsg}
          ref={ref}
          type={type}
          className={cn(
            inputVariants({
              prefix: !!prefix,
              suffix: !!suffix,
              fullWidth: !!fullWidth,
              error: !!errorMsg,
              className,
            }),
          )}
          {...inputProps}
          onChange={(e) => {
            const value = e.target.value;
            if (!value || type !== "number") {
              inputProps?.onChange?.(e);
              return;
            }

            // Prevent non-numeric characters and leading zeros
            const numberValue = Number(value);
            const min = inputProps?.min ? +inputProps.min : -Infinity;
            const max = inputProps?.max ? +inputProps.max : Infinity;

            const safeNumberValue = Math.min(Math.max(numberValue, min), max);
            inputProps?.onChange?.({
              ...e,
              target: {
                ...e.target,
                value: isNaN(safeNumberValue) ? "" : String(safeNumberValue),
              },
            });
          }}
        />

        {!!suffix && (
          <span
            data-testid={`${testId}-suffix`}
            className={cn(
              "tw-h-full tw-inline-flex tw-absolute tw-top-0 tw-right-12 tw-items-center tw-justify-center tw-w-20",
              {
                "tw-text-gray-500": inputProps.disabled,
              },
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
